// apps/web/src/components/vendas/material-combobox.tsx — Fase 12, Plano 07 (PV-01, D-05).
// Seletor de material interno com busca no servidor (mesmo padrão de cliente-combobox.tsx).
// Consulta GET /api/v1/vendas/lookups/materiais (pv:ler; até 20 materiais ativos, ordenados por código).
// Fase 13, Plano 11: a prop `endpoint` permite outra fonte (estoque usa /api/v1/estoque/lookups/materiais, estoque:consultar,
// e nunca exige pv:ler — RESEARCH Pitfall 8). O tipo é genérico: o lookup de estoque devolve campos a mais (unidade etc.).
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import type { MaterialLookupPv } from '@pieltec/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface MaterialComboboxProps<T extends MaterialLookupPv> {
  readonly value: T | null;
  readonly onChange: (material: T) => void;
  readonly disabled?: boolean;
  readonly invalido?: boolean;
  readonly id?: string;
  /** Rota do lookup (a busca vai em `?busca=`). Padrão: lookup de Vendas. */
  readonly endpoint?: string;
  /** Parâmetros fixos de query enviados junto da busca (ex.: `fornecedorId` no lookup de Compras). */
  readonly parametros?: Readonly<Record<string, string>>;
}

const ATRASO_BUSCA_MS = 250;
const ENDPOINT_PADRAO = '/api/v1/vendas/lookups/materiais';

export function MaterialCombobox<T extends MaterialLookupPv = MaterialLookupPv>({
  value,
  onChange,
  disabled = false,
  invalido = false,
  id,
  endpoint = ENDPOINT_PADRAO,
  parametros,
}: MaterialComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [materiais, setMateriais] = useState<readonly T[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const chaveParametros = JSON.stringify(parametros ?? {});

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const timer = window.setTimeout(() => {
      const termo = busca.trim();
      const params = new URLSearchParams(JSON.parse(chaveParametros) as Record<string, string>);
      if (termo) params.set('busca', termo);
      const query = params.toString();
      void fetch(`${endpoint}${query ? `?${query}` : ''}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          if (!res.ok) {
            setErro('Não foi possível carregar os materiais.');
            setMateriais([]);
            return;
          }
          setMateriais((await res.json()) as T[]);
        })
        .catch(() => {
          if (ativo) setErro('Não foi possível carregar os materiais.');
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [open, busca, endpoint, chaveParametros]);

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalido || undefined}
          className={`ui-control w-full flex items-center justify-between min-h-[var(--control-height,2rem)] px-3 py-2 bg-white border rounded-md text-sm text-slate-800 disabled:opacity-50 ${
            invalido ? 'border-rose-500' : 'border-slate-300'
          }`}
        >
          <span className="truncate">{value ? `${value.codigo} — ${value.nome}` : 'Selecione o material...'}</span>
          {disabled ? (
            <Loader2 className="w-4 h-4 text-slate-400 shrink-0 ml-2 animate-spin" aria-hidden="true" />
          ) : (
            <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" aria-hidden="true" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 overflow-hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
          <input
            ref={buscaRef}
            type="text"
            aria-label="Buscar material"
            placeholder="Buscar por código ou nome do material"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox" aria-label="Materiais">
          {carregando ? (
            <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Carregando materiais...
            </div>
          ) : erro ? (
            <div role="alert" className="p-4 text-center text-sm text-rose-700">
              {erro}
            </div>
          ) : materiais.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">Nenhum material encontrado.</div>
          ) : (
            materiais.map((material) => {
              const selecionado = value?.id === material.id;
              return (
                <button
                  key={material.id}
                  type="button"
                  role="option"
                  aria-selected={selecionado}
                  onClick={() => {
                    onChange(material);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`w-full min-h-[var(--control-height,2rem)] flex items-center justify-between px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    selecionado ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex flex-col truncate pr-2">
                    <span className="truncate">{material.nome}</span>
                    <span className={`text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>{material.codigo}</span>
                  </span>
                  {selecionado && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
